import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api, { errMsg } from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import { Footprints, Delete, Loader2, Crown } from 'lucide-react';

export default function Login() {
  const [users, setUsers] = useState([]);
  const [selected, setSelected] = useState(null);
  const [pin, setPin] = useState('');
  const [loading, setLoading] = useState(false);
  const { login, user } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    api.get('/auth/login-users').then((r) => setUsers(r.data)).catch(() => toast.error('Gagal memuat daftar user'));
  }, []);

  useEffect(() => {
    if (user) navigate('/', { replace: true });
  }, [user, navigate]);

  const press = (k) => {
    if (pin.length < 6) setPin(pin + k);
  };

  const submit = async () => {
    if (!selected || pin.length < 4) {
      toast.error('Pilih user dan masukkan PIN 4-6 digit');
      return;
    }
    setLoading(true);
    try {
      const u = await login(selected.id, pin);
      toast.success(`Selamat datang, ${u.name}!`);
      navigate('/', { replace: true });
    } catch (e) {
      toast.error(errMsg(e));
      setPin('');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4" data-testid="login-page">
      <div className="w-full max-w-md animate-fade-up">
        <div className="text-center mb-8">
          <div className="w-16 h-16 rounded-2xl bg-brand mx-auto flex items-center justify-center shadow-xl shadow-brand/30 animate-pulse-glow">
            <Footprints className="w-9 h-9 text-white" />
          </div>
          <h1 className="font-display text-4xl sm:text-5xl font-extrabold tracking-tight mt-4">LODISHOES</h1>
          <p className="text-muted-foreground text-sm mt-1">Point of Sale — Balaraja & Ciledug</p>
        </div>

        {!selected ? (
          <div className="space-y-3" data-testid="user-select-list">
            <p className="lodi-label text-center">Pilih akun Anda</p>
            {users.map((u) => (
              <button key={u.id} onClick={() => { setSelected(u); setPin(''); }} data-testid={`login-user-${u.id}`}
                className="w-full lodi-card p-4 flex items-center gap-4 text-left hover:border-brand/50 active:scale-[0.99]">
                <div className={`w-12 h-12 rounded-full flex items-center justify-center font-display font-bold text-lg ${
                  u.role === 'admin' ? 'bg-brand/15 text-brand' : u.branch === 'balaraja' ? 'bg-blue-500/15 text-blue-400' : u.branch === 'ciledug' ? 'bg-purple-500/15 text-purple-400' : 'bg-teal-500/15 text-teal-400'
                }`}>
                  {u.role === 'admin' ? <Crown className="w-6 h-6" /> : u.name.charAt(0)}
                </div>
                <div className="flex-1">
                  <p className="font-semibold">{u.name}</p>
                  <p className="text-xs text-muted-foreground">
                    {u.role === 'admin' ? 'Admin — Full Akses' : `Kasir — ${u.branch_name || u.branch || ''}`}
                  </p>
                </div>
              </button>
            ))}
            {users.length === 0 && <p className="text-center text-sm text-muted-foreground py-6">Memuat user...</p>}
          </div>
        ) : (
          <div className="space-y-5" data-testid="pin-entry">
            <button onClick={() => { setSelected(null); setPin(''); }} className="text-sm text-muted-foreground hover:text-foreground" data-testid="back-to-users">
              ← Ganti user
            </button>
            <div className="text-center">
              <p className="font-display font-bold text-xl">{selected.name}</p>
              <p className="text-xs text-muted-foreground">Masukkan PIN Anda</p>
            </div>
            <div className="flex justify-center gap-3" data-testid="pin-dots">
              {[...Array(6)].map((_, i) => (
                <div key={i} className={`w-3.5 h-3.5 rounded-full transition-all ${i < pin.length ? 'bg-brand scale-110' : 'bg-elevated border border-line'}`} />
              ))}
            </div>
            <div className="grid grid-cols-3 gap-3 max-w-[280px] mx-auto">
              {['1','2','3','4','5','6','7','8','9','','0'].map((k, i) =>
                k === '' ? <div key={i} /> : (
                  <button key={i} onClick={() => press(k)} data-testid={`pin-key-${k}`}
                    className="h-16 rounded-2xl bg-elevated border border-line text-2xl font-bold font-display hover:border-brand hover:text-brand transition-all active:scale-95">
                    {k}
                  </button>
                )
              )}
              <button onClick={() => setPin(pin.slice(0, -1))} data-testid="pin-key-delete"
                className="h-16 rounded-2xl bg-elevated border border-line flex items-center justify-center text-muted-foreground hover:text-brand hover:border-brand transition-all active:scale-95">
                <Delete className="w-6 h-6" />
              </button>
            </div>
            <button onClick={submit} disabled={loading || pin.length < 4} data-testid="login-submit"
              className="w-full py-3.5 rounded-xl bg-brand hover:bg-branddark disabled:opacity-40 text-white font-display font-bold tracking-wide transition-all active:scale-[0.98] shadow-lg shadow-brand/25 flex items-center justify-center gap-2">
              {loading && <Loader2 className="w-5 h-5 animate-spin" />} MASUK
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
